import { Component, Input, Output, OnChanges, SimpleChanges, SimpleChange, EventEmitter } from '@angular/core';
import { Observable, Subject } from 'rxjs/Rx';
import { MessageService } from 'base';
import { NetFacadeService } from 'netview';
import { ShareDto, ShareTypes, ShareAccessTypes } from 'UvExplorerServer.Dtos.TS/Shares';
import { IdentityService } from 'identity';
import { AccountType, getAccountTypeLabel, UserClass, UserDto } from '../../../UvExplorerServer.Dtos.TS';
import { ObjUtils } from '../../../DiscoveryEngine.TS/Utils';
import { GroupDto, Privileges } from '../../../UvExplorerServer.Dtos.TS/Auth';


@Component({
    selector: 'share-editor',
	templateUrl: './share-editor.component.html',
	styleUrls: ['./share-editor.component.scss']
})
export class ShareEditorComponent {

	@Input()
	public sharedItemGuid: string;

	@Input()
	public sourceUserGuid: string;

	@Input()
	public display: boolean;

	@Output()
	public displayChange: EventEmitter<any>;

	@Output()
	public onOK: EventEmitter<any>;

	@Output()
	public onCancel: EventEmitter<any>;

	public accessType: string;
	public privileges: Privileges;
	public privilegesEditorVisible: boolean;

	public userEntries: any[];
	public excludedUserGuids: string[];
	public userSelectorVisible: boolean;

	public groupEntries: any[];
	public excludedGroupGuids: string[];
	public groupSelectorVisible: boolean;

	private errorCount: number;

	constructor(private netFacadeService: NetFacadeService,
		private messageService: MessageService,
		private identityService: IdentityService) {

		this.sharedItemGuid = "";
		this.sourceUserGuid = "";
		this.display = false;
		this.displayChange = new EventEmitter<any>();
		this.onOK = new EventEmitter<any>();
		this.onCancel = new EventEmitter<any>();
		this.errorCount = 0;

		this.resetFields();
	}

	private resetFields(): void {
		this.accessType = ShareAccessTypes.View;
		this.privileges = new Privileges();
		this.privilegesEditorVisible = false;

		this.userEntries = [];
		this.excludedUserGuids = [this.identityService.session.UserGuid];
		this.userSelectorVisible = false;

		this.groupEntries = [];
		this.excludedGroupGuids = [];
		this.groupSelectorVisible = false;
	}

	public get hasUserEntries(): boolean {
		return (!!this.userEntries && this.userEntries.length > 0);
	}

	public get hasGroupEntries(): boolean {
		return (!!this.groupEntries && this.groupEntries.length > 0);
	}

	public get enableOK(): boolean {
		return (!!this.sharedItemGuid && !!this.sourceUserGuid && (this.hasUserEntries || this.hasGroupEntries));
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.resetFields();
	}

	public editPrivileges(): void {
		this.privilegesEditorVisible = true;
	}

	public addUsers(): void {
		this.userSelectorVisible = true;
	}

	public addSelectedUsers(selectedUsers: UserDto[]): void {
		if (!!selectedUsers && selectedUsers.length > 0) {
			selectedUsers.forEach((user: UserDto) => {
				if (!this.excludedUserGuids.find((userGuid: string) => userGuid === user.Guid)) {
					this.excludedUserGuids.push(user.Guid);
					this.addUserEntry(user);
				}
			});

			this.triggerUsersUpdate();
		}
	}

	private addUserEntry(user: UserDto): void {
		let entry = {
			username: user.Username,
			firstname: user.FirstName,
			lastname: user.LastName,
			class: UserClass[user.UserClass],
			type: getAccountTypeLabel(user.AccountType),
			active: (user.IsActive ? 'Yes' : 'No'),
			tag: user
		};

		this.userEntries.push(entry);
	}

	public removeUser(user: UserDto): void {
		ObjUtils.RemoveArrayElement(this.excludedUserGuids, user.Guid);
		ObjUtils.RemoveArrayElementPredicate(this.userEntries, (entry) => entry.tag.Guid === user.Guid);
		this.triggerUsersUpdate();
	}

	private triggerUsersUpdate(): void {
		this.userEntries = [...this.userEntries];
	}

	public addGroups(): void {
		this.groupSelectorVisible = true;
	}

	public addSelectedGroups(selectedGroups: GroupDto[]): void {
		if (!!selectedGroups && selectedGroups.length > 0) {
			selectedGroups.forEach((group: GroupDto) => {
				if (!this.excludedGroupGuids.find((groupGuid: string) => groupGuid === group.Guid)) {
					this.excludedGroupGuids.push(group.Guid);
					this.addGroupEntry(group);
				}
			});

			this.triggerGroupsUpdate();
		}
	}

	private addGroupEntry(group: GroupDto): void {
		if (!this.groupEntries.some((entry) => entry.tag.Guid === group.Guid)) {
			let entry = {
				groupname: group.Name,
				class: UserClass[group.UserClass],
				type: getAccountTypeLabel(group.AccountType),
				active: (group.IsActive ? 'Yes' : 'No'),
				tag: group,
			};

			this.groupEntries.push(entry);
		}
	}

	public removeGroup(group: GroupDto): void {
		ObjUtils.RemoveArrayElement(this.excludedGroupGuids, group.Guid);
		ObjUtils.RemoveArrayElementPredicate(this.groupEntries, (entry) => entry.tag.Guid === group.Guid);
		this.triggerGroupsUpdate();
	}

	private triggerGroupsUpdate(): void {
		this.groupEntries = [...this.groupEntries];
	}

	public handleOK(): void {
		this.errorCount = 0;
		this.createUserShares(0);
	}

	private createUserShares(i: number): void {
		if (i < this.userEntries.length) {
			let user: UserDto = this.userEntries[i].tag;
			this.createShare(user.Guid, "").subscribe(
				(result: ShareDto) => {
					this.createUserShares(i + 1);
				},
				(error) => {
					++this.errorCount;
				});
		}
		else {
			this.createGroupShares(0);
		}
	}

	private createGroupShares(i: number): void {
		if (i < this.groupEntries.length) {
			let group: GroupDto = this.groupEntries[i].tag;
			this.createShare("", group.Guid).subscribe(
				(result: ShareDto) => {
					this.createGroupShares(i + 1);
				},
				(error) => {
					++this.errorCount;
				});
		}
		else {
			this.hideDialog();
			this.onOK.emit(true);

			if (this.errorCount == 0) {
				let totalCount = this.userEntries.length + this.groupEntries.length;
				if (totalCount == 1) {
					this.messageService.displayInfoMessage(totalCount + " share was created");
				}
				else {
					this.messageService.displayInfoMessage(totalCount + " shares were created");
				}
			}
			else {
				if (this.errorCount == 1) {
					this.messageService.displayErrorMessage("Failed to create " + this.errorCount + " share");
				}
				else {
					this.messageService.displayErrorMessage("Failed to create " + this.errorCount + " shares");
				}
			}
		}
	}

	private createShare(targetUserGuid: string, targetGroupGuid): Observable<ShareDto> {

		let newShare = new ShareDto();
		newShare.ShareType = ShareTypes.Network;
		newShare.SharedItemGuid = this.sharedItemGuid;
		newShare.SourceUserGuid = this.sourceUserGuid;
		newShare.TargetUserGuid = targetUserGuid;
		newShare.TargetGroupGuid = targetGroupGuid;
		newShare.AccessType = this.accessType;
		newShare.Privileges.Union(this.privileges);

		return this.netFacadeService.createShare(newShare);
	}

	public handleCancel(): void {
		this.hideDialog();
		this.onCancel.emit(true);
	}

	public onDialogHide(): void {
		this.handleCancel();
	}

    public displayDialog() {
		this.display = true;
		this.displayChange.emit();
    }

    public hideDialog() {
        this.display = false;
		this.displayChange.emit();
    }

}
